import { useState, useEffect, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import HomeFooter from "@/pages/home/components/HomeFooter";
import { useNavigate } from "react-router-dom";

const steps = [
  { num: "01", title: "無料相談・お見積り", period: "無料 / 約1〜3日", icon: "ri-chat-smile-3-line", body: "「何から始めればいいかわからない」という段階でも大丈夫です。無料のオンラインミーティングにて、現状の課題やご要望をじっくりとお聞きします。どのサービスが自社に合うかわからない場合も、現状を分析したうえで最適な施策をご提案します。" },
  { num: "02", title: "ご契約・キックオフ", period: "約1〜2週間", icon: "ri-file-text-line", body: "お見積り内容にご納得いただけましたらご契約となります。契約後はキックオフミーティングを実施し、ビジネスの強み・ターゲット顧客・競合の状況・目指すゴールを詳しくヒアリング。施策全体の構成・優先順位・スケジュールをしっかり固めます。" },
  { num: "03", title: "施策実行・制作", period: "約3〜8週間", icon: "ri-code-s-slash-line", body: "キックオフで決定した方針をもとに、各施策を実行・制作していきます。ホームページ制作であればワイヤーフレーム・デザイン・コーディング、SEO・MEOであればコンテンツ制作・各種最適化設定まで一貫して対応。進捗を随時ご報告します。" },
  { num: "04", title: "確認・調整・スタート", period: "約1〜2週間", icon: "ri-check-double-line", body: "施策の内容をお客様と一緒に最終確認し、問題がなければ本格スタートします。スタート直後のデータも素早くキャッチし、必要に応じて即座に調整します。" },
  { num: "05", title: "継続サポート・改善", period: "継続サポート", icon: "ri-refresh-line", body: "ここからが本番です。クラプリックスでは施策開始後も継続的にサポートします。コンテンツの更新代行・SEO改善・サーバー管理など、サイトの成長に合わせてご支援。「作って終わり」ではなく、成果が出るまで一緒に動くパートナーを目指しています。" },
];

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Approach Section ── */
function ApproachSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="relative py-16 md:py-24 lg:py-32" style={{ background: "#ffffff" }}>
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* タイトル部分 - max-w制限なしで左寄せ */}
        <div className="px-6 md:px-10 lg:px-20">
          <div className="text-left mb-10 md:mb-16">
            <div className="relative inline-block">
              <h2
                className="font-bold leading-tight relative mb-3 md:mb-5"
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "clamp(38px, 6vw, 80px)",
                  color: "#111111",
                  letterSpacing: "-0.02em",
                  zIndex: 1,
                }}
              >
                FLOW
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <p
                className="font-bold leading-relaxed"
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "clamp(13px, 2.4vw, 30px)",
                  color: "#111111",
                }}
              >
                はじめての相談から、成果が出るまで。
              </p>
              <div style={{ width: "44px", height: "3px", background: "#c00000", flexShrink: 0 }} />
            </div>
          </div>
        </div>

        {/* 下の文章 - max-w-[1100px] で右寄せ */}
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-0">
          <div className="text-right ml-auto" style={{ maxWidth: "85%" }}>
            <div
              className="leading-loose"
              style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(13px, 1.4vw, 19px)", color: "#111111" }}
            >
              <span className="hidden md:inline">
                「何から始めればいいかわからない」という状態でも、心配いりません。<br />
                CRUPLIXは、無料相談からスタートし、戦略設計・制作・実行・改善まで、<br />
                すべてのステップをワンチームで伴走します。<br />
                あなたが迷わず前に進めるよう、プロセスを透明に、丁寧に説明しております。
              </span>
              <span className="md:hidden">
                「何から始めればいいかわからない」<br />という状態でも、心配いりません。<br /><br />
                CRUPLIXは無料相談からスタートし、<br />戦略設計・制作・実行・改善まで、<br />すべてのステップをワンチームで伴走します。<br /><br />
                あなたが迷わず前に進めるよう、<br />プロセスを透明に、丁寧に説明しております。
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineLine() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "scaleY(0)";
    el.style.transformOrigin = "top";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.style.transition = "transform 1.5s cubic-bezier(0.22,1,0.36,1)";
        el.style.transform = "scaleY(1)";
        obs.unobserve(el);
      }
    }, { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="absolute left-6 md:left-8 top-0 bottom-0 w-px" style={{ background: "#e0e0e0" }} />
  );
}

function StepItem({ s, idx, isOpen, onToggle }: {
  s: typeof steps[0];
  idx: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateX(-30px)";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setTimeout(() => {
          if (!el) return;
          el.style.transition = "opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1)";
          el.style.opacity = "1";
          el.style.transform = "translateX(0)";
        }, idx * 100);
        obs.unobserve(el);
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [idx]);

  return (
    <div ref={ref} className="relative pl-20 md:pl-28">
      <button
        className="absolute left-0 top-6 w-12 h-12 md:w-20 md:h-20 flex items-center justify-center cursor-pointer z-10 border-2"
        style={{
          background: isOpen ? "#c00000" : "#ffffff",
          borderColor: isOpen ? "#c00000" : "#e0e0e0",
          transition: "background 0.3s, border-color 0.3s, transform 0.3s",
          transform: isOpen ? "scale(1.1)" : "scale(1)",
        }}
        onClick={onToggle}
      >
        <i
          className={`${s.icon} text-xl md:text-2xl`}
          style={{ color: isOpen ? "#ffffff" : "#111111", transition: "color 0.3s" }}
        />
      </button>
      <div
        className="overflow-hidden cursor-pointer"
        style={{ border: `1px solid ${isOpen ? "#c00000" : "#e0e0e0"}`, transition: "border-color 0.3s" }}
        onClick={onToggle}
      >
        <div
          className="flex items-center justify-between px-8 py-8 md:px-12 md:py-10"
          style={{ background: isOpen ? "#f5f5f5" : "#ffffff", transition: "background 0.3s" }}
        >
          <div className="flex items-center gap-4 md:gap-6">
            <span
              className="font-light counter-num"
              style={{ fontFamily: "'DM Mono', monospace", fontWeight: 300, color: "#c00000", fontSize: "clamp(24px, 2.6vw, 40px)" }}
            >{s.num}</span>
            <div>
              <div className="font-bold" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111", fontSize: "clamp(16px, 1.8vw, 24px)" }}>{s.title}</div>
              <div className="mt-1 md:mt-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111", fontSize: "clamp(12px, 1.1vw, 14px)" }}>{s.period}</div>
            </div>
          </div>
          <i
            className="ri-arrow-down-s-line text-2xl md:text-3xl"
            style={{ color: "#111111", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)" }}
          />
        </div>
        <div className={`faq-answer px-8 md:px-12 ${isOpen ? "open" : ""}`} style={{ background: "#f5f5f5" }}>
          <p className="py-8 md:py-10 leading-loose" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111", fontSize: "clamp(14px, 1.3vw, 16px)" }}>{s.body}</p>
        </div>
      </div>
      {idx < steps.length - 1 && (
        <div className="flex justify-start ml-6 md:ml-10 mt-4 mb-4 md:mt-6 md:mb-6">
          <i className="ri-arrow-down-line text-xl md:text-2xl float-arrow" style={{ color: "#111111" }} />
        </div>
      )}
    </div>
  );
}

function CtaSection() {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-10 md:py-20 px-4 md:px-6" style={{ background: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}>
      <div ref={ref} className="reveal max-w-5xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-sm tracking-[0.4em] font-medium" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>CONTACT</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-3" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>お問い合わせ</h2>
          <div className="mx-auto mt-4" style={{ width: "32px", height: "2px", background: "#c00000" }} />
          <p className="text-xs md:text-sm leading-loose mt-5 mx-auto whitespace-nowrap" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
            ご不明点などございましたらお気軽にお問い合わせください。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-6">
          <div
            className="flex flex-col items-center text-center p-4 md:p-8 cursor-pointer"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onClick={() => navigate("/contact")}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#c00000"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#f0f0f0" }}>
              <i className="ri-mail-line text-base md:text-xl" style={{ color: "#111111" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>MAIL</h3>
            <span className="text-xs md:text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>
              <span className="hidden md:inline">フォームを開く</span> <i className="ri-arrow-right-line" />
            </span>
          </div>
          <a
            href="https://lin.ee/tP9rADP"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center text-center p-4 md:p-8"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#06C755"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#06C755" }}>
              <i className="ri-line-fill text-base md:text-xl" style={{ color: "#ffffff" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>LINE</h3>
            <span className="text-xs md:text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#06C755" }}>
              <span className="hidden md:inline">LINEを開く</span> <i className="ri-external-link-line" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function FlowPage() {
  const [active, setActive] = useState<number | null>(0);

  return (
    <PageLayout>
      <ApproachSection />
      <section className="py-12 md:py-20 lg:py-28 px-4 md:px-6 lg:px-16 xl:px-24" style={{ background: "#ffffff" }}>
        <div className="max-w-[1600px] mx-auto">
          <div className="relative">
            <TimelineLine />
            <div className="flex flex-col gap-6">
              {steps.map((s, idx) => (
                <StepItem
                  key={s.num}
                  s={s}
                  idx={idx}
                  isOpen={active === idx}
                  onToggle={() => setActive(active === idx ? null : idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaSection />

      <HomeFooter />
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}